/**
 * Soft/hard currency anchors for Blind Kings.
 * Coins = match faucet (online / ranked / bot); gems = Stripe + flat per-level gifts.
 * Local games grant no XP/coins.
 *
 * Match payout (online / ranked): STANDARD × outcome × lengthMult × modeMult
 *   Outcome: win 1.0 · loss 0.40
 *   Length: too short 0 · short 0.50 · medium/long 1.00
 *   Mode: online 1.00 · ranked 1.15
 *
 * Bot (AI) payout: STANDARD × outcome × lengthMult × difficultyMult
 *   easy 0.55 · medium 0.75 · hard 1.00
 *   Wins skip the length cut. Losses use the same length table as online.
 *
 * Store sinks use 4× coin scale vs early economy (gems unchanged).
 * GEMS_PER_LEVEL = 10; new accounts start at 0 coins / 0 gems.
 */

import { GAME_LENGTHS, GAME_MODES } from '../consts/game.js';

/** XP for a regular-length online win (before boosts). */
export const STANDARD_GAME_XP = 750;

/** Coins for a regular-length online win (before boosts). */
export const STANDARD_GAME_COINS = 72;

/** Flat gem gift on every level-up. */
export const GEMS_PER_LEVEL = 10;

export const MATCH_OUTCOME_WIN = 1;
export const MATCH_OUTCOME_LOSS = 0.4;

export const MATCH_LENGTH_MULT = {
  [GAME_LENGTHS.TOO_SHORT]: 0,
  [GAME_LENGTHS.SHORT_GAME]: 0.5,
  [GAME_LENGTHS.MEDIUM_GAME]: 1,
  [GAME_LENGTHS.LONG_GAME]: 1,
};

export const MATCH_MODE_MULT = {
  [GAME_MODES.LOCAL_GAME]: 0,
  [GAME_MODES.ONLINE_GAME]: 1,
  [GAME_MODES.RANKED_GAME]: 1.15,
  // AI uses BOT_DIFFICULTY_MULT instead of this flat value.
  [GAME_MODES.AI_GAME]: 1,
};

/** Bot difficulty payout scale (replaces the flat AI mode mult). */
export const BOT_DIFFICULTY_MULT = {
  easy: 0.55,
  medium: 0.75,
  hard: 1,
};

/** True when this mode can grant match XP/coins (local excluded). */
export function rewardsEnabledForMode(gameMode = '') {
  const mode = String(gameMode ?? '');
  return (
    mode === GAME_MODES.ONLINE_GAME ||
    mode === GAME_MODES.RANKED_GAME ||
    mode === GAME_MODES.AI_GAME
  );
}

export function resolveBotDifficultyMult(botDifficulty = 'medium') {
  const key = String(botDifficulty ?? 'medium').toLowerCase();
  return BOT_DIFFICULTY_MULT[key] ?? BOT_DIFFICULTY_MULT.medium;
}

/**
 * Match XP/coins before personal boosts.
 * Online/ranked: outcome × length × mode.
 * AI/bot: outcome × difficulty; losses also apply the length table.
 */
export function computeMatchRewards({
  won = false,
  gameLength = GAME_LENGTHS.MEDIUM_GAME,
  gameMode = GAME_MODES.ONLINE_GAME,
  botDifficulty = 'medium',
} = {}) {
  if (!rewardsEnabledForMode(gameMode)) {
    return {
      xp: 0,
      coins: 0,
      outcomeBase: 0,
      lengthMult: 0,
      modeMult: 0,
      scale: 0,
      gameLength,
      gameMode: String(gameMode ?? ''),
      botDifficulty: undefined,
    };
  }

  const outcomeBase = won ? MATCH_OUTCOME_WIN : MATCH_OUTCOME_LOSS;
  const isAi = String(gameMode) === GAME_MODES.AI_GAME;
  const applyLengthCut = !isAi || !won;
  const lengthMult = applyLengthCut ? (MATCH_LENGTH_MULT[gameLength] ?? 1) : 1;
  const modeMult = isAi
    ? resolveBotDifficultyMult(botDifficulty)
    : (MATCH_MODE_MULT[gameMode] ?? 1);
  const scale = outcomeBase * lengthMult * modeMult;

  const xp = Math.max(0, Math.round(STANDARD_GAME_XP * scale));
  // Keep coin payout locked to the same scale (and XP ratio) as the XP award.
  const coins = Math.max(
    0,
    Math.round(STANDARD_GAME_COINS * scale),
  );

  return {
    xp,
    coins,
    outcomeBase,
    lengthMult,
    modeMult,
    scale,
    gameLength,
    gameMode: String(gameMode ?? ''),
    ...(isAi ? { botDifficulty: String(botDifficulty ?? 'medium') } : {}),
  };
}

/**
 * Gems granted when the player reaches `level` (called per level crossed).
 * Flat `GEMS_PER_LEVEL` each time — real-time taper comes from longer XP gaps.
 * Coins come from matches only — no level-up coin gifts.
 */
export function getLevelUpReward(_level = 1) {
  return { coins: 0, gems: GEMS_PER_LEVEL };
}

/**
 * Sum level-up gifts for every level in (fromLevel, toLevel].
 */
export function sumLevelUpRewards(fromLevel = 1, toLevel = 1) {
  const from = Math.max(1, Math.floor(Number(fromLevel) || 1));
  const to = Math.max(from, Math.floor(Number(toLevel) || from));
  let coins = 0;
  let gems = 0;
  const levels = [];
  for (let level = from + 1; level <= to; level += 1) {
    const reward = getLevelUpReward(level);
    coins += reward.coins;
    gems += reward.gems;
    levels.push({ level, ...reward });
  }
  return { coins, gems, levels };
}
